import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api, track } from "@/lib/api";
import { SmartImage } from "@/components/site/SmartImage";

const pick = (offers, name) =>
  (name ? offers.find((o) => o.display_name === name) : offers[0]) || null;

/**
 * Two product types shown side by side, each one its own shopping path. The image and the button
 * carry the same affiliate link, and the merchant comes from the offer record so it is named
 * before either is clicked.
 */
export const MerchantCompare = ({ data, articleSlug, id = "compare-options" }) => {
  const [offers, setOffers] = useState({});

  useEffect(() => {
    const cats = [...new Set((data.cards || []).map((c) => c.category))];
    Promise.all(
      cats.map((c) =>
        api
          .get("/offers/group", { params: { category: c } })
          .then((r) => [c, r.data.offers || []])
          .catch(() => [c, []])
      )
    ).then((pairs) => setOffers(Object.fromEntries(pairs)));
  }, [data]);

  return (
    <section id={id} data-testid="merchant-compare" className="mt-12">
      <span className="eyebrow">{data.heading}</span>
      {data.intro && (
        <p className="text-[16.5px] md:text-[17.5px] leading-[1.6] text-[#3F3F3F] mt-4 max-w-3xl">
          {data.intro}
        </p>
      )}

      <div className="grid md:grid-cols-2 gap-px bg-line mt-7">
        {(data.cards || []).map((c) => {
          const offer = pick(offers[c.category] || [], c.display_name);
          const slug = c.category;
          const onClick = () =>
            track({
              event_type: "cta_click",
              cta_placement: "GUIDE COMPARE CARD",
              offer_category: c.category,
              article_slug: articleSlug,
              offer_id: offer?.id,
              merchant: offer?.merchant,
            });

          return (
            <div key={slug} data-testid={`compare-card-${slug}`} className="bg-white flex flex-col">
              {offer ? (
                <a
                  href={offer.tracking_url}
                  target="_blank"
                  rel="sponsored nofollow noopener"
                  aria-label={`${c.eyebrow}: ${c.label} — explore at ${offer.merchant}`}
                  data-testid={`compare-image-link-${slug}`}
                  onClick={onClick}
                  className="group block bg-[#F4F2ED] aspect-[4/3] overflow-hidden"
                >
                  <SmartImage
                    src={c.image}
                    alt={c.image_alt}
                    sizes="(min-width:1024px) 40vw, 100vw"
                    maxWidth={768}
                    className="w-full h-full object-contain p-5 transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
                  />
                </a>
              ) : (
                <div className="bg-[#F4F2ED] aspect-[4/3] overflow-hidden">
                  <SmartImage
                    src={c.image}
                    alt={c.image_alt}
                    sizes="(min-width:1024px) 40vw, 100vw"
                    maxWidth={768}
                    className="w-full h-full object-contain p-5"
                  />
                </div>
              )}

              <div className="px-6 md:px-7 py-7 flex flex-col flex-1">
                <span className="text-[11.5px] tracking-[0.16em] uppercase text-[#48563F] font-bold">
                  {c.eyebrow}
                </span>
                <h3 className="text-[18px] md:text-[19px] font-medium leading-snug text-charcoal mt-3">
                  {c.label}
                </h3>
                <p className="text-[15.5px] md:text-[16px] leading-[1.6] text-[#454545] mt-3">
                  {c.note}
                </p>
                {offer && (
                  <div className="mt-auto pt-7">
                    <a
                      href={offer.tracking_url}
                      target="_blank"
                      rel="sponsored nofollow noopener"
                      data-testid={`compare-cta-${slug}`}
                      onClick={onClick}
                      className="inline-flex items-center gap-2 px-6 py-3.5 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300"
                    >
                      {offer.cta_label} <ArrowUpRight size={15} />
                    </a>
                    <p className="text-[12px] text-[#6E6A63] mt-3">
                      Affiliate link · opens at{" "}
                      <span className="font-semibold text-[#3F3F3F]">{offer.merchant}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {data.note && (
        <p className="text-[13px] text-[#6E6A63] mt-5 max-w-3xl">{data.note}</p>
      )}
      {data.back_link && (
        <Link
          to={data.back_link.to}
          data-testid="merchant-compare-back-link"
          className="inline-flex items-center gap-2 mt-6 text-[12.5px] tracking-[0.14em] uppercase font-semibold text-[#48563F] hover:text-sage-dark transition-colors duration-300"
        >
          {data.back_link.label} <ArrowRight size={15} />
        </Link>
      )}
    </section>
  );
};
